import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { PropertiesSearchService, UnitDetailsResponse } from '../../../services/properties-search.service';

export interface SearchResult {
  unit_details: {
    property_name: string;
    unit_no: string;
    property_unit_no: string;
    rent: string;
    deposit: string;
    availability_status: string;
    vacant_date: string;
    available_date_for_booking: string | null;
    type: string;
  };
  contracts: {
    active?: any[];
    inactive?: any[];
    draft?: any[];
    hold?: any[];
    closed?: any[];
    cancelled?: any[];
    history?: any[];
  };
  bookings?: any[];
  booking_details?: any[];
  total_contracts?: number;
  total_bookings?: number;
}

@Component({
  selector: 'app-dynamic-search-dialog',
  templateUrl: './dynamic-search-dialog.component.html',
  styleUrls: ['./dynamic-search-dialog.component.scss']
})
export class DynamicSearchDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;

  searchForm: FormGroup;
  searchTypes = [
    { value: 'properties', label: 'Properties' }
    // More search types will be added here later
  ];

  searchResult: SearchResult | null = null;
  isLoading = false;
  error: string | null = null;

  // Status icons mapping
  statusIcons = {
    active: 'check_circle',
    inactive: 'pause_circle',
    draft: 'edit',
    hold: 'pause',
    closed: 'check_circle_outline',
    cancelled: 'cancel'
  };

  constructor(
    public dialogRef: MatDialogRef<DynamicSearchDialogComponent>,
    private propertiesSearchService: PropertiesSearchService
  ) {
    this.searchForm = new FormGroup({
      searchType: new FormControl('properties', [Validators.required]),
      searchQuery: new FormControl('', [Validators.required])
    });
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // Focus on search input after view is initialized with longer delay for dialog animation
    this.focusSearchInput();
  }

  private focusSearchInput(): void {
    // Use multiple attempts to ensure focus works after dialog animation
    setTimeout(() => {
      if (this.searchInput?.nativeElement) {
        this.searchInput.nativeElement.focus();
        this.searchInput.nativeElement.select();
      }
    }, 200);
  }

  ngOnDestroy(): void {
  }

  onSearch(): void {
    if (this.searchForm.valid) {
      const { searchType, searchQuery } = this.searchForm.value;

      this.isLoading = true;
      this.error = null;
      this.searchResult = null;

      switch (searchType) {
        case 'properties':
          this.searchProperties(searchQuery);
          break;
        // Add more search types here later
        default:
          this.error = 'Invalid search type';
          this.isLoading = false;
      }
    }
  }

  private searchProperties(query: string): void {
    this.propertiesSearchService.searchUnitDetails(query).subscribe({
      next: (response: UnitDetailsResponse) => {
        this.isLoading = false;
        if (response.status && response.data) {
          // Deduplicate contracts in each category
          this.searchResult = this.deduplicateContracts(response.data);
        } else {
          this.error = response.message || 'No results found';
        }
      },
      error: (error) => {
        this.isLoading = false;

        // Handle different error scenarios
        if (error.status === 422) {
          // Unit not found - specific error message
          this.error = error.error?.message || 'Unit not found. Please check the property unit number and try again.';
        } else if (error.status === 401) {
          this.error = 'Authentication required. Please log in again.';
        } else if (error.status === 403) {
          this.error = 'Access denied. You don\'t have permission to search properties.';
        } else if (error.status === 500) {
          this.error = 'Server error. Please try again later.';
        } else if (error.status === 0) {
          this.error = 'Network error. Please check your internet connection.';
        } else {
          this.error = error.error?.message || error.message || 'An error occurred while searching. Please try again.';
        }
      }
    });
  }

  getNonEmptyContractGroups(): { title: string, status: string, contracts: any[] }[] {
    if (!this.searchResult || !this.searchResult.contracts) return [];

    const statusOrder = ['active', 'draft', 'hold', 'inactive', 'closed', 'cancelled', 'history'];
    const groups = [];

    for (const status of statusOrder) {
      if (this.searchResult.contracts[status] && this.searchResult.contracts[status].length > 0) {
        groups.push({
          title: status === 'history' ? 'Past' : status.charAt(0).toUpperCase() + status.slice(1),
          status: status,
          contracts: this.searchResult.contracts[status]
        });
      }
    }

    return groups;
  }

  getStatusIcon(status: string): string {
    return this.statusIcons[status] || 'info';
  }

  // Error handling methods
  getErrorIcon(): string {
    if (this.error?.includes('Unit not found')) return 'search_off';
    if (this.error?.includes('Authentication')) return 'lock';
    if (this.error?.includes('Access denied')) return 'block';
    if (this.error?.includes('Server error')) return 'dns';
    if (this.error?.includes('Network error')) return 'wifi_off';
    return 'error';
  }

  getErrorTitle(): string {
    if (this.error?.includes('Unit not found')) return 'Unit Not Found';
    if (this.error?.includes('Authentication')) return 'Authentication Error';
    if (this.error?.includes('Access denied')) return 'Access Denied';
    if (this.error?.includes('Server error')) return 'Server Error';
    if (this.error?.includes('Network error')) return 'Connection Error';
    return 'Search Error';
  }

  canRetry(): boolean {
    // Allow retry for server errors, network errors, and general errors
    return !this.error?.includes('Authentication') &&
           !this.error?.includes('Access denied') &&
           !this.error?.includes('Unit not found');
  }

  onRetry(): void {
    if (this.searchForm.valid) {
      this.error = null;
      this.onSearch();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onClear(): void {
    this.searchForm.reset();
    this.searchForm.patchValue({ searchType: 'properties' });
    this.searchResult = null;
    this.error = null;
  }

  getBookingStatusIcon(status: string): string {
    const iconMap = {
      'InProgress': 'hourglass_empty',
      'Completed': 'check_circle',
      'Cancelled': 'cancel',
      'Pending': 'schedule',
      'Confirmed': 'verified'
    };
    return iconMap[status] || 'bookmark';
  }

  calculateDuration(startDate: string, endDate: string): string {
    if (!startDate || !endDate) return '';

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      const remainingDays = diffDays % 30;
      return remainingDays > 0 ? `${months} months, ${remainingDays} days` : `${months} months`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const months = Math.floor(remainingDays / 30);
      return `${years} year${years > 1 ? 's' : ''}${months > 0 ? `, ${months} months` : ''}`;
    }
  }

  /**
   * Remove duplicate contracts from search results based on contract ID or contract_no
   */
  private deduplicateContracts(data: SearchResult): SearchResult {
    if (!data || !data.contracts) {
      return data;
    }

    const dedupeArray = (arr: any[]): any[] => {
      if (!arr || !Array.isArray(arr)) return arr;
      const seen = new Set();
      return arr.filter(item => {
        const key = item.id || item.contract_no;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    };

    return {
      ...data,
      contracts: {
        active: dedupeArray(data.contracts.active),
        inactive: dedupeArray(data.contracts.inactive),
        draft: dedupeArray(data.contracts.draft),
        hold: dedupeArray(data.contracts.hold),
        closed: dedupeArray(data.contracts.closed),
        cancelled: dedupeArray(data.contracts.cancelled),
        history: dedupeArray(data.contracts.history)
      }
    };
  }
}
